import RemindOutlineIcon from '@rsuite/icons/RemindOutline'
import {useRef} from 'react'
import Modal from 'rsuite/Modal'
import styled from 'styled-components'

/**
 * Confirmation dialog shared by ~29 screens. It is the last thing an operator sees
 * before a record is removed or a sync job is fired at core banking, so it is built
 * around one idea: name the action, repeat that same word on the button, and never
 * say "Ok".
 *
 * Severity is carried structurally rather than by colour. The tenant's brand colour is
 * itself red (`theme/themes.json`) and `.btn-primary` is `!important`-locked to it in
 * `theme/globalStyles.ts`, so a red confirm button would read as "brand", not "danger".
 * Instead the caution mark appears *only* for destructive actions -- its presence is the
 * signal, and a routine confirmation (sync) reads calm because the mark is absent.
 */

const DESTRUCTIVE_VERBS = ['delete', 'remove', 'discard', 'reject', 'revoke', 'deactivate']

let dialogCount = 0

const Dialog = styled(Modal)`
  .rs-modal-content {
    padding: 0;
    border-radius: 10px;
    overflow: hidden;
  }

  .rs-modal-header {
    padding: 24px 24px 0;
    margin: 0;
  }

  .rs-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 18px 24px;
    margin: 22px 0 0;
    border-top: 1px solid #eaecf0;
    background: #fcfcfd;
  }

  .confirm-dialog__prompt {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  /* Presence of this mark is the severity signal -- see the note at the top of the file. */
  .confirm-dialog__mark {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    margin-top: 1px;
    border-radius: 9px;
    background: #fffaeb;
    box-shadow: inset 0 0 0 1px #fedf89;
    color: #b54708;
  }

  .confirm-dialog__question {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.45;
    color: #101828;
  }

  .confirm-dialog__consequence {
    margin: 6px 0 0;
    max-width: 46ch;
    font-size: 14px;
    line-height: 1.55;
    color: #667085;
  }

  /* Not .btn-secondary: globalStyles paints that with the tenant's secondary colour,
     which is green in the shipped theme -- a green Cancel beside a red confirm. */
  .btn.confirm-dialog__cancel {
    color: #344054;
    background: #fff;
    border: 1px solid #d0d5dd;
  }

  .btn.confirm-dialog__cancel:hover {
    color: #101828;
    background: #f9fafb;
  }

  .rs-modal-footer .btn {
    min-width: 88px;
    font-size: 13px;
    font-weight: 600;
  }

  .rs-modal-footer .btn:focus-visible {
    outline: 2px solid #101828;
    outline-offset: 2px;
  }
`

interface Props {
  isOpen?: boolean
  handleClick: () => void
  handleClose: () => void
  /** The action being confirmed, e.g. `sync` or `delete this form`. Defaults to `delete`. */
  title?: string
  /** Confirm button label. Defaults to the first word of `title`, capitalised. */
  confirmLabel?: string
  /** Line under the question. Defaults to "This can't be undone." when destructive. */
  description?: string
  /** Overrides the destructive heuristic when the verb alone isn't enough. */
  destructive?: boolean
}

const DeleteModal = ({
  isOpen,
  handleClick,
  handleClose,
  title,
  confirmLabel,
  description,
  destructive,
}: Props) => {
  // React 17 has no useId, and two of these can sit in one tree (a delete dialog and a
  // sync dialog on the same page), so the aria wiring needs ids that cannot collide.
  const idRef = useRef<number>()
  if (idRef.current === undefined) idRef.current = ++dialogCount
  const titleId = `confirm-dialog-title-${idRef.current}`
  const descriptionId = `confirm-dialog-description-${idRef.current}`

  const action = title?.trim() || 'delete'
  const verb = action.split(/\s+/)[0].toLowerCase()
  const isDestructive = destructive ?? DESTRUCTIVE_VERBS.includes(verb)

  // Sentence case: the old copy rendered "...want to Delete?" with a capital mid-sentence.
  const question = `Are you sure you want to ${action.charAt(0).toLowerCase()}${action.slice(1)}?`
  const consequence = description ?? (isDestructive ? "This can't be undone." : undefined)
  const confirmText = confirmLabel ?? `${verb.charAt(0).toUpperCase()}${verb.slice(1)}`

  return (
    <Dialog
      backdrop='static'
      role='alertdialog'
      aria-labelledby={titleId}
      aria-describedby={consequence ? descriptionId : undefined}
      open={isOpen}
      onClose={handleClose}
      size='xs'
      enforceFocus={false}
      data-cy='modal'
    >
      <Modal.Header closeButton={false}>
        <div className='confirm-dialog__prompt'>
          {isDestructive && (
            <span className='confirm-dialog__mark' aria-hidden='true'>
              <RemindOutlineIcon style={{fontSize: 18}} />
            </span>
          )}
          <div>
            <h5 className='confirm-dialog__question' id={titleId} data-cy='modal-title'>
              {question}
            </h5>
            {consequence && (
              <p className='confirm-dialog__consequence' id={descriptionId}>
                {consequence}
              </p>
            )}
          </div>
        </div>
      </Modal.Header>

      <Modal.Footer>
        {/* Cancel comes first in the DOM so it is the first tab stop, and sits left of the
            confirm button. Nothing is auto-focused: rsuite focuses the dialog itself, which
            is what announces the alert and lets it restore focus to the trigger on close
            (Overlay/Modal.js only records the last focus when it moves focus itself). */}
        <button
          type='button'
          onClick={handleClose}
          className='btn btn-sm confirm-dialog__cancel'
          data-cy='modal-cancel'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={handleClick}
          className='btn btn-primary btn-sm'
          data-cy='modal-submit'
        >
          {confirmText}
        </button>
      </Modal.Footer>
    </Dialog>
  )
}

export default DeleteModal
